"use client";

import React, { useEffect, useState } from "react";
import Image from "next/image";
import weblogo from "../../../public/kd-logo-new.png";
// import Lenis from "@studio-freight/lenis";
import { Bars3Icon } from "@heroicons/react/24/outline";
import { XMarkIcon } from "@heroicons/react/20/solid";
import DropDownMenu from "./dropDownMenu";
import Link from "next/link";
import { usePathname } from "next/navigation";
import AnimatedButton from "../ui/AnimatedButton";
import { isSgLandingPage } from "@/utils/isSgLandingPage";

type OverlayState = "closed" | "menu" | "contact";
type OverlayMode = Exclude<OverlayState, "closed">;

export default function Header({
    icons,
    links,
    contact,
    homepageFormData,
}: {
    icons: any;
    links: any;
    contact: string;
    homepageFormData?: any;
}) {
    const [overlayState, setOverlayState] = useState<OverlayState>("closed");
    const [renderedOverlayMode, setRenderedOverlayMode] =
        useState<OverlayMode>("menu");
    const [isTop, setIsTop] = useState(true);
    const [scrimOpacity, setScrimOpacity] = useState(0);
    const [activeCode, setActiveCode] = useState("");
    const [prefix, setPrefix] = useState("");
    const isOverlayOpen = overlayState !== "closed";

    const pathname = usePathname();

    const isSGHomepage = isSgLandingPage(pathname);

    const navLinks = [
        { label: "ABOUT US", href: "/about" },
        { label: "OUR EXPERTISE", href: "/services" },
        { label: "OUR WORK", href: "/works" },
    ];

    const setOverlay = (nextState: OverlayState) => {
        if (nextState !== "closed") {
            setRenderedOverlayMode(nextState);
        }

        setOverlayState(nextState);
    };

    const toggleOverlay = (openState: OverlayMode) => {
        setOverlayState((prev) => {
            const nextState = prev === openState ? "closed" : openState;

            if (nextState !== "closed") {
                setRenderedOverlayMode(nextState);
            }

            return nextState;
        });
    };

    useEffect(() => {
        setOverlayState("closed");
    }, [pathname]);

    useEffect(() => {
        if (typeof document === "undefined") return;
        const isDesktop = window.matchMedia("(min-width: 768px)").matches;
        const html = document.documentElement;
        const body = document.body;
        const prevHtmlOverflow = html.style.overflow;
        const prevBodyOverflow = body.style.overflow;

        if (isOverlayOpen && isDesktop) {
            html.style.overflow = "hidden";
            body.style.overflow = "hidden";
        } else {
            html.style.overflow = prevHtmlOverflow;
            body.style.overflow = prevBodyOverflow;
        }

        return () => {
            html.style.overflow = prevHtmlOverflow;
            body.style.overflow = prevBodyOverflow;
        };
    }, [isOverlayOpen]);

    useEffect(() => {
        if (typeof window !== "undefined") {
            const hostname = window.location.hostname;

            if (hostname.includes("my") || hostname.includes("localhost")) {
                setActiveCode("MY");
            } else if (hostname.includes("sg")) {
                setActiveCode("SG");
            } else {
                setActiveCode("");
            }

            const pre =
                hostname.includes("staging") || hostname.includes("localhost")
                    ? "staging."
                    : "";
            setPrefix(pre);
        }
    }, []);

    // Scroll listener for top state
    useEffect(() => {
        if (pathname === "/404") {
            setIsTop(false);
            return;
        }

        // Fade the gradient in gradually over this distance, instead of
        // snapping to full opacity the moment scrollY leaves 0.
        const scrimFadeDistance = 150;

        const handleScroll = () => {
            setIsTop(window.scrollY === 0);
            setScrimOpacity(Math.min(window.scrollY / scrimFadeDistance, 1));
        };

        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, [pathname]);

    // Country links
    const countryLinks = [
        { code: "MY", href: `https://${prefix}kingdomdigital.com.my/` },
        { code: "SG", href: `https://${prefix}kingdomdigital.com.sg/` },
    ];

    return (
        <header className="fixed top-0 left-0 right-0 w-full flex p-3 z-50">
            {isSGHomepage && (
                // Scroll scrim for the SG landing page: replaces the
                // per-element purple pills with one gradient bar behind the
                // whole navbar, so individual items just sit transparent.
                <div
                    className="fixed inset-x-0 top-0 w-full h-24 md:h-28 z-30 pointer-events-none bg-gradient-to-b from-black/60 to-transparent"
                    style={{ opacity: scrimOpacity }}
                />
            )}
            <a
                href="/"
                aria-label="Kingdom Digital"
                className={`relative z-40 duration-200 px-[1.15rem] pt-[0.7rem] pb-[0.53rem] rounded-full ${
                    isSGHomepage
                        ? "bg-transparent"
                        : isTop
                          ? "bg-transparent"
                          : "bg-[#851bed8c] backdrop-blur-md"
                }`}
            >
                <Image
                    src={weblogo}
                    alt="Kingdom Digital Logo"
                    width={100}
                    height={100}
                    className="min-w-[50px] w-[50px] object-contain"
                    priority
                />
            </a>

            {isSGHomepage && (
                <nav className="hidden md:flex items-center gap-x-10 fixed left-1/2 -translate-x-1/2 top-6 z-40">
                    {navLinks.map((link) => (
                        <Link
                            key={link.href}
                            href={link.href}
                            className="px-4 py-2 rounded-full text-white text-sm font-medium tracking-wide hover:text-turquoise transition-colors duration-200 bg-transparent"
                        >
                            {link.label}
                        </Link>
                    ))}
                </nav>
            )}

            <div
                className={`flex items-center border border-white/30 rounded-2xl fixed right-20 md:right-[4.5rem] top-4 z-[110] !bg-transparent transition-all duration-300 
        ${
            overlayState === "menu"
                ? "opacity-100 translate-y-0"
                : "opacity-0 -translate-y-4 pointer-events-none"
        }
    `}
            >
                {countryLinks.map(({ code, href }) => {
                    const isActive = code === activeCode;
                    return (
                        <Link
                            key={code}
                            href={href}
                            target="_blank"
                            className={`group flex items-center justify-center px-3 py-1.5 border rounded-2xl transition-all duration-300 
                  ${
                      isActive
                          ? "bg-turquoise"
                          : "border border-transparent hover:border-turquoise hover:bg-turquoise/10"
                  }`}
                        >
                            <span
                                className={`font-medium transition-colors duration-300 
                    ${
                        isActive
                            ? "text-black"
                            : "text-white group-hover:text-turquoise"
                    }`}
                            >
                                {code}
                            </span>
                        </Link>
                    );
                })}
            </div>

            {/* CTA + Burger Menu */}
            <div className="fixed right-3 top-3 z-[100] flex items-center">
                <div
                    className={`block scale-[0.9] md:scale-100 origin-right transition-all duration-200 ${
                        isOverlayOpen ? "opacity-0 pointer-events-none" : ""
                    }`}
                >
                    <AnimatedButton
                        text="Get In Touch"
                        link="#get-in-touch"
                        showArrow
                        buttonClassName="!px-6"
                        onClick={() => toggleOverlay("contact")}
                    />
                </div>

                <div
                    className={`flex items-center duration-200 pt-[0.35rem] pr-[0.35rem] pb-[0.35rem] pl-[0.35rem] rounded-full cursor-pointer ml-2 ${
                        isOverlayOpen || isSGHomepage
                            ? "bg-transparent"
                            : isTop
                              ? "bg-transparent"
                              : "bg-[#851bed8c] backdrop-blur-md"
                    }`}
                    role="button"
                    onClick={() =>
                        isOverlayOpen
                            ? setOverlay("closed")
                            : setOverlay("menu")
                    }
                >
                    <span className="hidden md:block text-white relative overflow-hidden">
                        {/* <span
                            className={`${
                                isOpen ? "-translate-y-[100%]" : "translate-y-0"
                            } duration-300 flex`}
                        >
                            Menu
                        </span> */}
                        <span
                            className={`${
                                isOverlayOpen
                                    ? "translate-y-0"
                                    : "translate-y-[100%]"
                            } absolute bottom-0 left-0 right-0 duration-300`}
                        >
                            Close
                        </span>
                    </span>

                    <div className="relative p-[5px] border border-white rounded-full">
                        <Bars3Icon
                            className={`text-white w-5 h-auto aspect-square ${
                                isOverlayOpen ? "opacity-0" : "opacity-100"
                            } duration-150`}
                        />
                        <XMarkIcon
                            className={`text-white w-5 h-auto aspect-square ${
                                isOverlayOpen ? "opacity-100" : "opacity-0"
                            } duration-150 absolute top-0 bottom-0 left-0 right-0 m-auto`}
                        />
                    </div>
                </div>
            </div>

            {/* Dropdown Menu */}
            <DropDownMenu
                isOpen={isOverlayOpen}
                mode={renderedOverlayMode}
                links={links}
                icons={icons.iconLink}
                contact={contact}
                homepageFormData={homepageFormData}
            />
        </header>
    );
}
